import { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Icon } from "@/components";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

export interface MenuItem {
	icon?: IconDefinition;
	label: string;
	onClick: () => void;
}

interface ScoringPickerMenuProps {
	anchorEl: HTMLElement;
	items: MenuItem[];
	onClose: () => void;
	width?: number;
	portalContainer?: HTMLElement | null;
}

/**
 * A small anchored action menu (PREC-4055) — the matrix corner's "Add" menu and
 * the scoring card's copy menu. Portaled, `position: fixed`, closes on outside
 * click (via `composedPath`, web rule §11) and Escape.
 */
export const ScoringPickerMenu: FunctionComponent<ScoringPickerMenuProps> = ({
	anchorEl,
	items,
	onClose,
	width = 240,
	portalContainer,
}) => {
	const menuRef = useRef<HTMLDivElement>(null);
	const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

	useLayoutEffect(() => {
		const update = () => {
			const rect = anchorEl.getBoundingClientRect();

			setPosition({
				top: rect.bottom + 4,
				left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)),
			});
		};

		update();
		window.addEventListener("scroll", update, true);
		window.addEventListener("resize", update);

		return () => {
			window.removeEventListener("scroll", update, true);
			window.removeEventListener("resize", update);
		};
	}, [anchorEl, width]);

	useEffect(() => {
		const onMouseDown = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (!menuRef.current?.contains(target) && !anchorEl.contains(target)) {
				onClose();
			}
		};
		const onKeyDown = (event: KeyboardEvent) => event.key === "Escape" && onClose();

		document.addEventListener("mousedown", onMouseDown);
		document.addEventListener("keydown", onKeyDown);

		return () => {
			document.removeEventListener("mousedown", onMouseDown);
			document.removeEventListener("keydown", onKeyDown);
		};
	}, [anchorEl, onClose]);

	return createPortal(
		<div
			ref={menuRef}
			className="fixed z-[1600] rounded-lg border border-border-light bg-white p-1 shadow-lg"
			data-testid="action-menu"
			role="menu"
			style={{ top: position?.top ?? -9999, left: position?.left ?? -9999, width }}
		>
			{items.map((item) => (
				<button
					key={item.label}
					className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-text hover:bg-background-light"
					role="menuitem"
					type="button"
					onClick={() => {
						item.onClick();
						onClose();
					}}
				>
					{item.icon && <Icon className="h-3 w-3 text-text-disabled" icon={item.icon} />}
					{item.label}
				</button>
			))}
		</div>,
		portalContainer ?? document.body
	);
};
